import { FC, useContext, useEffect, useRef, useState } from "react";
import Breadcrumbs from "@app/shared/breadcrumbs/Breadcrumbs";
import ErrorBoundary from "@app/shared/ErrorBoundary";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import Practitioner from "@app/network-outreach/components/Practitioner";
import PractitionerAtLocation from "@app/network-outreach/components/PractitionerAtLocation";
import ServiceLocation from "@app/network-outreach/components/ServiceLocation";
import { NetworkOutreachEmbeddedModel } from "@app/network-outreach/models";
import OutreachService from "@app/network-outreach/outreachService";
import { OutreachViews } from "@app/network-outreach/models/outreachViews";
import { QESButton } from "@app/shared/qes-standards/Button";

export const NetworkOutreachPage: FC = () => {
  const user = useContext(UserContext);
  const exportServiceRef = useRef<OutreachService>(null);
  if (exportServiceRef.current === null) {
    exportServiceRef.current = new OutreachService();
  }
  const exportService = exportServiceRef.current;
  const outreachDetailViews: OutreachViews[] = [
    "Service Location",
    "Practitioner",
    "Practitioner At Location"
  ];

  const [activeView, setActiveView] = useState<OutreachViews>("Service Location");
  const [currentQuarter, setCurrentQuarter] = useState<string>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReportValid, setIsReportValid] = useState(false);
  const [reportSettings, setReportSettings] = useState<NetworkOutreachEmbeddedModel>(null);

  useEffect(() => {
    let isValid = false;

    exportService.getNetworkOutreachConfiguration().then((settings) => {
      isValid = validateSettings(settings);
      setReportSettings(settings);
    }).catch(() => {
      isValid = false;
    }).finally(() => {
      setIsLoading(false);
      setIsReportValid(isValid);
    });
  }, []);

  const getActiveView = () => {
    switch (activeView) {
      case "Service Location":
        return (
          <div className="col-12">
            <PageSection>
              {isReportValid ? (
                <ServiceLocation {...reportSettings} name="Service Location" pageName={reportSettings.serviceLocationPageName} setCurrentQuarter={setCurrentQuarter} currentQuarter={currentQuarter} selectorId={reportSettings.networkOutreachPeriodSelectorId} />
              ) : (
                <h3> Unable to display report at this time.</h3>
              )}
            </PageSection>
          </div>
        );
      case "Practitioner":
        return (
          <div className="col-12">
            <PageSection>
              {isReportValid ? (
                <Practitioner {...reportSettings} name="Practitioner" pageName={reportSettings.practitionerPageName} setCurrentQuarter={setCurrentQuarter} currentQuarter={currentQuarter} selectorId={reportSettings.networkOutreachPeriodSelectorId} />
              ) : (
                <h3> Unable to display report at this time.</h3>
              )}
            </PageSection>
          </div>
        );
      case "Practitioner At Location":
        return (
          <div className="col-12">
            <PageSection>
              {isReportValid ? (
                <PractitionerAtLocation {...reportSettings} name="Practitioner At Location" pageName={reportSettings.practitionerAtLocationPageName} setCurrentQuarter={setCurrentQuarter} currentQuarter={currentQuarter} selectorId={reportSettings.networkOutreachPeriodSelectorId} />
              ) : (
                <h3> Unable to display report at this time.</h3>
              )}
            </PageSection>
          </div>
        );
      default:
        return (
          <div className="col-12">
            <PageSection>
              <h3> Unable to display report at this time.</h3>
            </PageSection>
          </div>
        );
    }
  }

  const validateSettings = (settings: NetworkOutreachEmbeddedModel) => {
    if (settings == null) {
      return false;
    }

    let isValid = true;

    if (settings.reportId == null || settings.reportId.length == 0) {
      isValid = false;
    }

    if (settings.token == null || settings.token.length == 0) {
      isValid = false;
    }

    if (settings.url == null || settings.url.length == 0) {
      isValid = false;
    }

    if (settings.clientId == null || settings.clientId.length == 0) {
      isValid = false;
    }

    if (!settings.minimumQuarter) {
      isValid = false;
    }

    return isValid;
  }

  return (
    <RoleRestricted allowedRoles={[Roles.NETWORK_OUTREACH_USER]}>
      <ErrorBoundary user={user}>
        <Page documentTitle="Network Outreach">
          <Breadcrumbs items={[{ text: "Network Outreach" }]} />
          <PageInner>
            <PageHeader title="Network Outreach" disableHR={true} />
            <PageSection>
              <div className="row">
                <div className="col-12">
                  <div className="tabs-style-2">
                    <ul className="nav nav-tabs">
                      {outreachDetailViews.map((key) => (
                        <li className="nav-item" key={key}>
                          <QESButton qesType="none" qesTestId={`network-outreach-tab--${key}`} className={`nav-link${activeView === key ? " active" : ""}`} aria-selected={activeView === key} role="tab" onClick={() => setActiveView(key)}>{key}</QESButton>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="row mt-3">
                    {isLoading ?
                      (
                        <div className="row">
                          <div className="col-12">
                            <div className="text-center">
                              <LoadingSpinner />
                            </div>
                          </div>
                        </div>
                      )
                      : getActiveView()}
                  </div>
                </div>
              </div>
            </PageSection>
          </PageInner>
        </Page>
      </ErrorBoundary>
    </RoleRestricted>
  );
}
